import { action, computed, observable } from 'mobx'
import { distinctUntilChanged, filter, type Observable, pairwise } from 'rxjs'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import type { ErrorDescriptor } from '@/features/legends/editor/analyzer-errors'
import { manageEach } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import { Types } from '@/features/legends/editor/ryno-types'
import {
  mustHaveItems,
  type Validatable,
  validateFields,
} from '@/features/legends/editor/validation'
import { shallowItemsAreEqual } from '@/utils/array-util'
import { tapAction, toStream } from '@/utils/mobx-util'
import { validateElements, when } from '@/utils/validx-util'
import {
  SymbolConnectorViewModel,
  type SymbolPredicate,
} from './SymbolConnectorViewModel'

export interface SymbolConnectorListViewModelOptions {
  dependencies: LegendPartDependencies
  context: SymbolContext
  predicates?: SymbolPredicate[]
  required?: boolean
}

/**
 * A list of symbol references, like a mutator's targets. Each entity can be
 * picked once, and the first connector decides the list's value type.
 */
export class SymbolConnectorListViewModel extends LegendPartViewModelBase {
  private readonly _context: SymbolContext
  private readonly _predicates: SymbolPredicate[]
  private readonly _isRequired: boolean
  private readonly _schema: IValidationSchema<SymbolConnectorListViewModel> = {
    connectors: [
      validateElements(),
      when(() => this._isRequired, mustHaveItems('Add at least one symbol.')),
    ],
  }

  readonly connectors = observable.array<SymbolConnectorViewModel>([], {
    deep: false,
  })

  readonly validation = validationContext(this, this._schema)

  constructor({
    dependencies,
    context,
    predicates,
    required,
  }: SymbolConnectorListViewModelOptions) {
    super(dependencies)
    this._context = context
    this._predicates = predicates ?? []
    this._isRequired = !!required
    if (this._isRequired) {
      this.add()
    }
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.connectors))
  }

  /**
   * The first connector's type, which the others must match.
   */
  @computed
  get valueType() {
    return this.connectors.length > 0
      ? this.connectors[0].valueType
      : Types.nothing
  }

  /**
   * The number of connectors.
   */
  @computed
  get count() {
    return this.connectors.length
  }

  /**
   * Sets the referenced IDs from the legend document.
   */
  @action
  hydrate(symbols: string[] | null | undefined = []) {
    this.connectors.clear()
    for (const target of symbols ?? []) {
      this.add().hydrate(target)
    }
    if (this.connectors.length === 0 && this._isRequired) {
      this.add()
    }
    return this
  }

  /**
   * Adds an empty connector.
   */
  @action.bound
  add() {
    const connector: SymbolConnectorViewModel = new SymbolConnectorViewModel({
      dependencies: this.dependencies,
      required: true,
      context: this._context,
      predicates: [
        ...this._predicates,
        // An entity picked by another connector in the list is not offered.
        (descriptor) =>
          !this.connectors.some(
            (c) => c !== connector && c.symbolId === descriptor.entity.id,
          ),
      ],
    })
    this.connectors.push(connector)
    return connector
  }

  /**
   * Adds an empty connector and focuses it.
   */
  @action.bound
  addAndFocus() {
    this.add()
    this.focus()
  }

  /**
   * Removes the connector.
   */
  @action.bound
  remove(connector: SymbolConnectorViewModel) {
    this.connectors.remove(connector)
  }

  /**
   * Focuses the last connector.
   */
  focus() {
    this.connectors[this.connectors.length - 1]?.focus()
  }

  /**
   * Revalidates the target's field when the first connector's symbol
   * changes, since it decides the value type, while the returned stream is
   * subscribed; pass it to the target's lifecycle. Adding or removing the
   * first connector doesn't count as a change.
   *
   * @param target
   * @param schema The schema the target's validation context is bound to.
   * @param field
   */
  bubbleErrorsTo<T>(
    target: T & Validatable,
    schema: IValidationSchema<T>,
    field: keyof T & string,
  ): Observable<unknown> {
    return toStream(() => {
      const connector = this.connectors.at(0) ?? null
      return [connector, connector?.symbol?.id, connector?.symbol?.valueType]
    }, true).pipe(
      distinctUntilChanged(shallowItemsAreEqual),
      pairwise(),
      filter(([previous, current]) => previous[0] === current[0]),
      tapAction(() => validateFields(target, schema, [field])),
    )
  }

  /**
   * Shows the analyzer's errors for each target on its connector.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    for (const [index, connector] of this.connectors.entries()) {
      const errors = descriptor.inner?.[index]?.errors ?? []
      if (errors.length > 0) {
        connector.validation.addErrors({ symbol: errors })
      }
    }
  }

  /**
   * The referenced IDs that are still available.
   */
  toJSON(): string[] {
    return this.connectors
      .map((c) => c.toJSON())
      .filter((id): id is string => !!id)
  }
}
